import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Refresh, DonutLarge } from '@mui/icons-material';
import { eventBus, EventTypes } from '@core/pubsub/EventBus';
import { dashboardService } from '@/services/dashboardService';
import { ShopId } from '@core/store/slices/apiSlice';

import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { BaseWidget } from '@/components/widgets/BaseWidget.tsx';
import { useNavigate } from 'react-router-dom';
import { CountCapacityData } from '@core/types';

interface TrolleyShelterCountCapacityProps {
    shopIdName: ShopId;
    deviceId: string;
    moduleId: string;
    heading: string;
    className?: string;
}

const TrolleyShelterCountCapacity: React.FC<TrolleyShelterCountCapacityProps> = ({
    shopIdName,
    deviceId,
    moduleId,
    heading,
    className
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [countCapacity, setCountCapacity] = useState<CountCapacityData>({ currentCount: 0, capacity: 0 });

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const result = await dashboardService.loadTrolleyShelterCountCapacity({ deviceId, moduleId });
            setCountCapacity(result || { currentCount: 0, capacity: 0 });
        } catch (error) {
            console.error('Failed to load trolley shelter count capacity:', error);
            setCountCapacity({ currentCount: 0, capacity: 0 });
        } finally {
            setLoading(false);
        }
    }, [deviceId, moduleId]);

    useEffect(() => {
        load();

        const unsubscribe = eventBus.on(EventTypes.WIDGETS_RELOAD, load);

        return unsubscribe;
    }, [load]);

    const handleDailyDetailsClick = () => {
        navigate(`/statistics/rfid/dailydetails?shopId=${shopIdName.id}&moduleId=${moduleId}`);
    };

    const menuActions = [
        {
            text: t('refresh'),
            icon: <Refresh fontSize="small" />,
            onClick: load
        },
        {
            text: t('dailyDetails'),
            icon: <DonutLarge fontSize="small" />,
            onClick: () => {
                handleDailyDetailsClick();
            }
        }
    ];

    // Calculate percentage and prepare chart data
    const percentage = countCapacity.capacity ? (countCapacity.currentCount / countCapacity.capacity) * 100 : 0;

    const series = [percentage];

    const options: ApexOptions = {
        chart: {
            type: 'radialBar',
            height: 300,
            toolbar: {
                show: false
            },
            background: 'transparent',
            fontFamily: 'Wanzl_Etelka Light, Roboto, sans-serif'
        },
        plotOptions: {
            radialBar: {
                startAngle: -100,
                endAngle: 100,
                track: {
                    startAngle: -100,
                    endAngle: 100
                },
                dataLabels: {
                    value: {
                        fontSize: '25px',
                        offsetY: -10,
                        formatter: () => {
                            const current = countCapacity.currentCount !== undefined ? countCapacity.currentCount : '-';
                            const capacity = countCapacity.capacity !== undefined ? countCapacity.capacity : '-';
                            return `${current} / ${capacity}`;
                        }
                    }
                }
            }
        },
        colors: [percentage >= 50 ? '#8ac926' : '#D9534F'],
        labels: [''],
        theme: {
            mode: 'light'
        }
    };

    return (
        <BaseWidget
            heading={heading}
            icon={<DonutLarge />}
            className={className}
            customActions={menuActions}
            loading={loading}
        >
            <div style={{ marginTop: '-40px' }}>
                <Chart
                    key={`${countCapacity.currentCount}-${countCapacity.capacity}`}
                    options={options}
                    series={series}
                    type="radialBar"
                    height={300}
                />
            </div>
        </BaseWidget>
    );
};

export default TrolleyShelterCountCapacity;
